// Codex: every slice building and ship with its numbers (spec 2026-10-06 §3.3).
import type { FacilityType, ShipType } from '@stars-and-ships/api-types';
import { Info } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router';
import { useCodex } from '../api/queries';
import { BuildingInfo } from '../components/codex/BuildingInfo';
import { ShipInfo } from '../components/codex/ShipInfo';
import { ErrorNotice, Page, Panel, Spinner } from '../components/ui';
import { FACILITY_LABELS, SHIP_LABELS } from '../labels';

function EntryButton({ label, onClick }: { label: string; onClick(): void }) {
  return (
    <button
      type="button"
      aria-label={`About ${label}`}
      onClick={onClick}
      className="flex min-h-11 w-full items-center justify-between gap-2 py-2 text-left text-sm text-text hover:text-accent"
    >
      {label}
      <Info className="size-4 text-muted" aria-hidden />
    </button>
  );
}

export function CodexScreen() {
  const { galaxyId = '' } = useParams();
  const codex = useCodex(galaxyId);
  const [building, setBuilding] = useState<FacilityType | null>(null);
  const [ship, setShip] = useState<ShipType | null>(null);
  return (
    <Page width="narrow">
      {codex.isPending ? <Spinner /> : null}
      {codex.error ? <ErrorNotice error={codex.error} /> : null}
      {codex.data ? (
        <>
          <Panel title="Buildings">
            <ul className="divide-y divide-line">
              {codex.data.buildings.map((b) => (
                <li key={b.type}>
                  <EntryButton
                    label={FACILITY_LABELS[b.type]}
                    onClick={() => setBuilding(b.type)}
                  />
                </li>
              ))}
            </ul>
          </Panel>
          <Panel title="Ships">
            <ul className="divide-y divide-line">
              {codex.data.ships.map((s) => (
                <li key={s.type}>
                  <EntryButton label={SHIP_LABELS[s.type]} onClick={() => setShip(s.type)} />
                </li>
              ))}
            </ul>
          </Panel>
        </>
      ) : null}
      {building ? (
        <BuildingInfo
          galaxyId={galaxyId}
          facility={building}
          open
          onClose={() => setBuilding(null)}
        />
      ) : null}
      {ship ? (
        <ShipInfo galaxyId={galaxyId} ship={ship} open onClose={() => setShip(null)} />
      ) : null}
    </Page>
  );
}
